/**
 * `Pagination` - cursor-based "Next page" control.
 *
 * Rendered inside `AccountTable`'s footer slot, below the scrolling body,
 * so it stays visible while the rows scroll.
 *
 * M1 is forward-only because cursors are HMAC-signed and the upstream does
 * not expose a stable "previous" cursor on first-page requests. `< Prev` is
 * rendered for visual consistency with the wireframe but disabled at M1;
 * revisit at M2 when status / provider facets land.
 *
 * Accessibility:
 *   - Wrapped in `<nav aria-label="Members list pagination">`, per the MUR "Design Tokens and Accessibility" page
 *     Section 3's ARIA table. Without the landmark, screen-reader users
 *     cannot jump to the pager.
 *   - Disabled controls carry `aria-disabled="true"` alongside `disabled`,
 *     so they are still announced rather than skipped silently.
 *   - "Showing X to Y" sits in an `aria-live="polite"` region that updates
 *     after the fetch settles, announcing the new page boundary without
 *     interrupting.
 */

import { Button } from './button'

export interface PaginationProps {
  readonly currentCount: number
  readonly canFetchMore: boolean
  readonly isFetchingMore: boolean
  readonly onNext: () => void
}

export function Pagination({
  currentCount,
  canFetchMore,
  isFetchingMore,
  onNext,
}: PaginationProps): JSX.Element {
  const status = isFetchingMore
    ? 'Loading next page'
    : currentCount > 0
      ? `Showing 1 to ${currentCount}${canFetchMore ? ', more available' : ''}`
      : ''

  return (
    <nav
      aria-label="Members list pagination"
      className="flex items-center justify-between border-t border-border-default bg-bg-surface px-murmuration-4 py-murmuration-3"
    >
      <span aria-live="polite" className="text-murmuration-label text-text-secondary">
        {status}
      </span>
      <div className="flex items-center gap-murmuration-3">
        <Button
          size="sm"
          aria-disabled="true"
          disabled
          title="Previous page is not available at M1"
        >
          {'< Prev'}
        </Button>
        <Button
          size="sm"
          aria-disabled={canFetchMore && !isFetchingMore ? 'false' : 'true'}
          disabled={!canFetchMore || isFetchingMore}
          onClick={() => {
            if (canFetchMore && !isFetchingMore) onNext()
          }}
        >
          {isFetchingMore ? 'Loading...' : 'Next >'}
        </Button>
      </div>
    </nav>
  )
}
